import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db2026 } from "../config/firebase";
import { getActiveDb } from "../config/tournamentRuntime";
import { golfPoolService } from "./golfPoolService";
import { normalizePoolTeamNames } from "../utils/normalizePoolTeamNames";

const getDb = () => getActiveDb() || db2026;

// Tournament Operations
export const createTournament = async (tournamentData) => {
  const tournamentsRef = collection(getDb(), "tournaments");
  return await addDoc(tournamentsRef, {
    ...tournamentData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateTournament = async (tournamentId, data) => {
  const tournamentRef = doc(getDb(), "tournaments", tournamentId);
  return await updateDoc(tournamentRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const getTournament = async (tournamentId) => {
  const tournamentRef = doc(getDb(), "tournaments", tournamentId);
  const docSnap = await getDoc(tournamentRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
};

// Team Operations
export const addTeamToTournament = async (tournamentId, teamData) => {
  const teamsRef = collection(getDb(), "tournaments", tournamentId, "teams");
  return await addDoc(teamsRef, {
    ...teamData,
    createdAt: serverTimestamp(),
  });
};

export const getTeams = async (tournamentId) => {
  const teamsRef = collection(getDb(), "tournaments", tournamentId, "teams");
  const querySnapshot = await getDocs(teamsRef);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Score Operations
export const submitScore = async (tournamentId, scoreData) => {
  const scoresRef = collection(getDb(), "tournaments", tournamentId, "scores");
  return await addDoc(scoresRef, {
    ...scoreData,
    timestamp: serverTimestamp(),
  });
};

export const getScores = async (tournamentId, roundNumber) => {
  const scoresRef = collection(getDb(), "tournaments", tournamentId, "scores");
  const q = query(
    scoresRef,
    where("round", "==", roundNumber),
    orderBy("timestamp", "desc")
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Golf Pool Operations
export const placeBet = async (tournamentId, betData) => {
  const betsRef = collection(getDb(), "tournaments", tournamentId, "bets");
  return await addDoc(betsRef, {
    ...betData,
    timestamp: serverTimestamp(),
  });
};

export const getBets = async (tournamentId) => {
  const betsRef = collection(getDb(), "tournaments", tournamentId, "bets");
  const querySnapshot = await getDocs(betsRef);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Golf Pool Entry Operations
export const createGolfPoolEntry = async (entryData) => {
  const normalizedEntry = {
    ...entryData,
    teams: normalizePoolTeamNames(entryData.teams),
  };
  golfPoolService.validatePoolEntryData(normalizedEntry);

  const entriesRef = collection(getDb(), "pool_entries");
  return await addDoc(entriesRef, {
    ...normalizedEntry,
    timestamp: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
};

export const getGolfPoolEntries = async () => {
  const entriesRef = collection(getDb(), "pool_entries");
  const q = query(entriesRef, orderBy("timestamp", "desc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      teams: normalizePoolTeamNames(data.teams),
    };
  });
};

// User Profile Operations
export const updateUserProfile = async (userId, profileData) => {
  const userRef = doc(getDb(), "users", userId);
  return await updateDoc(userRef, {
    ...profileData,
    updatedAt: serverTimestamp(),
  });
};

export const getUserProfile = async (userId) => {
  const userRef = doc(getDb(), "users", userId);
  const docSnap = await getDoc(userRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
};
